import React from 'react';
import {
  Container,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SeoMetaTags from '../components/SeoMetaTags';
import useWpPage from '../hooks/useWpPage';

const FAQ = () => {
  // Get the page data for SEO
  const { pageData, isLoading } = useWpPage('faq');

  const faqItems = [
    {
      question: "How do I choose the right lawyer for my case?",
      answer: "When selecting a lawyer, consider their experience in your specific legal area, their track record, communication style, and fee structure. Don't hesitate to schedule consultations with multiple attorneys to find the best fit for your needs."
    },
    {
      question: "What should I bring to my first consultation?",
      answer: "Bring all relevant documents related to your case, including contracts, correspondence, police reports, medical records, and any other paperwork. Also prepare a list of questions and be ready to discuss your goals and concerns."
    },
    {
      question: "How are legal fees typically structured?",
      answer: "Legal fees can be structured as hourly rates, flat fees, contingency fees (where we only get paid if you win), or retainer agreements. The fee structure depends on the type of case and complexity involved."
    },
    {
      question: "How long will my case take?",
      answer: "The timeline varies greatly depending on the type of case, its complexity, and whether it settles out of court or goes to trial. During your consultation, we can provide a more specific timeline based on your particular situation."
    },
    {
      question: "Will my case go to trial?",
      answer: "Many cases are resolved through negotiation or alternative dispute resolution methods before reaching trial. However, we always prepare your case as if it will go to trial to ensure the best possible outcome."
    },
    {
      question: "Can I handle my legal matter without a lawyer?",
      answer: "While it's possible to represent yourself in some simple matters, legal issues can be complex with long-lasting consequences. Having experienced legal representation often leads to better outcomes and can save you time and money in the long run."
    }
  ];

  if (isLoading) {
    return (
      <Container>
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <SeoMetaTags
        title={pageData?.title || "Frequently Asked Questions"}
        description={pageData?.description || "Find answers to common legal questions and learn more about our legal services."}
        slug="faq"
      />
      
      <Container maxWidth="lg">
        <Box sx={{ py: 6 }}>
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 700,
              textAlign: 'center',
              mb: 4
            }}
          >
            Frequently Asked Questions
          </Typography>

          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              mb: 6,
              color: 'text.secondary',
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            {pageData?.content || 
             "Here are answers to some of the most common questions we receive about our legal services and processes."}
          </Typography>

          <Box sx={{ maxWidth: 'md', mx: 'auto' }}>
            {faqItems.map((item, index) => (
              <Accordion
                key={index}
                sx={{
                  mb: 2,
                  '&:before': {
                    display: 'none',
                  },
                  boxShadow: 1,
                  borderRadius: 1,
                  '&.Mui-expanded': {
                    boxShadow: 2,
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: 'grey.50',
                    '&.Mui-expanded': {
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                    },
                    '& .MuiAccordionSummary-content': {
                      margin: '16px 0',
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {item.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    {item.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Still Have Questions?
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
              Don't hesitate to reach out to us for personalized answers to your legal questions.
            </Typography>
            <Box
              component="a"
              href="/contact-us"
              sx={{
                display: 'inline-block',
                px: 4,
                py: 2,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                textDecoration: 'none',
                borderRadius: 1,
                fontWeight: 600,
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'translateY(-2px)',
                  boxShadow: 3
                }
              }}
            >
              Contact Us Today
            </Box>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default FAQ;
